// Date helpers. Days (an air date, a release date, a day picked in a date
// input) are plain "YYYY-MM-DD" strings, parsed by hand to avoid UTC shifts
// from `new Date("2026-06-12")`. Timestamps (a log's start and finish,
// `createdAt`) are UTC moments, shown on the viewer's day: through <Time>,
// or `formatTime` / `formatTimeShort` where only a string will do.
// src/timestamps.test.ts keeps them out of everything else.
import { Day, Timestamp } from "../types/Timestamp";

/** The months, whole: "January" to "December". */
export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** The months, short: "Jan" to "Dec". */
export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const isoDay = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;

export const todayISO = (): string => isoDay(new Date());

// The day `n` days before today, in the user's own time zone.
export const daysAgoISO = (n: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return isoDay(d);
};

// Today as Home's greeting says it: "Thursday 1 October".
export const formatToday = (): string =>
  new Date().toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

// A bare day where a timestamp should be: a log dated before logs were
// moments, in an offline copy of an older response. It's a day already.
const isBareDay = (at: Timestamp): boolean => at.length === 10;

// A timestamp's day where the viewer is, not in UTC.
export const localDay = (at: Timestamp): Day =>
  isBareDay(at) ? at : isoDay(new Date(at));

// A timestamp's date and time where the viewer is, in their locale's
// words ("1 Oct 2026, 21:12"); none for a bare day, which has no time.
export const localDateTime = (at: Timestamp): string | undefined =>
  isBareDay(at)
    ? undefined
    : new Date(at).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });

// A timestamp as a string, on the viewer's day: "1 Oct 2026".
export const formatTime = (at: Timestamp | null): string | null =>
  at ? formatDate(localDay(at)) : null;

// The same, short: "1 Oct" this year, "1 Oct 2025" in any other.
export const formatTimeShort = (at: Timestamp): string =>
  formatDay(localDay(at));

// How long ago a timestamp was, for a conversation: "just now", "5m ago",
// "2h ago", "3d ago", and its short day ("1 Oct") from a week on. A bare
// day, which has no time, is its day.
export const formatTimeAgo = (at: Timestamp, now = Date.now()): string => {
  if (isBareDay(at)) return formatTimeShort(at);
  const minutes = Math.floor((now - Date.parse(at)) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days < 7 ? `${days}d ago` : formatTimeShort(at);
};

// "18 Sep" this year, "18 Sep 2025" in any other.
export const formatDay = (iso: Day): string => {
  const [year, month, day] = iso.split("-").map(Number);
  const short = `${day} ${MONTHS[month - 1]}`;
  return year === new Date().getFullYear() ? short : `${short} ${year}`;
};

export const formatDate = (iso: Day | null): string | null => {
  if (!iso) return null;
  const [year, month, day] = iso.split("-").map(Number);
  if (!year || !month || !day) return null;
  return `${day} ${MONTHS[month - 1]} ${year}`;
};

/** The viewer's time zone ("America/Los_Angeles"), for the API to cut its
 *  days and years where the viewer does. */
export const timeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone;
